import { Box, Button, Stack } from "@mui/material";
import { defaultTo, isNil, maxBy } from "lodash";
import type { FC } from "react";
import { useState } from "react";
import type { ProductResponse } from "../../../admin/products/productsApi";
import dayjs, { ISO_DATE_FORMAT } from "../../../dayjs";
import type { OrderProduct } from "../../orderTypes";
import type { PackageProduct } from "./AddPackageProducts";
import AddPackageProducts from "./AddPackageProducts";
import { AddProductFromMapDialog } from "../../../admin/products/map/AddProductFromMapDialog";

export interface Product {
  readonly id: string;
  readonly date: string;
}

interface Tag {
  readonly category: string;
  readonly value: string;
}

interface Props {
  readonly values: Product[];
  readonly onChange: (value: OrderProduct, idx: number) => void;
  readonly onAddFromPackage: (values: OrderProduct[], tags: Tag[]) => void;
}

const AddProducts: FC<Props> = ({ values, onChange, onAddFromPackage }) => {
  const [mapOpen, setMapOpen] = useState(false);

  const getNextDate = (products: Product[], daysShift = 1): string => {
    const productDates = products
      .map((p) => p.date)
      .filter((d) => !isNil(d))
      .map((d) => dayjs(d, ISO_DATE_FORMAT));
    const max = maxBy(productDates, (m) => m.valueOf());
    return defaultTo(max, dayjs()).add(daysShift, "day").format(ISO_DATE_FORMAT);
  };

  const handleAddFromPackage = (products: PackageProduct[], startDate: string, tags: Tag[]) => {
    onAddFromPackage(
      products.map((p) => ({
        id: p.id,
        date: dayjs(startDate, ISO_DATE_FORMAT).add(p.daysShift, "days").format(ISO_DATE_FORMAT),
        options: p.options.map((id) => ({ id, quantity: 0 })),
      })),
      tags,
    );
  };

  const handleAddFromMap = (product: ProductResponse, date?: string) => {
    onChange({ id: product.id, date: date ?? getNextDate(values), options: [] }, values.length);
    setMapOpen(false);
  };

  return (
    <>
      <Stack direction="row" spacing={2}>
        <Button onClick={() => onChange({ id: "", date: getNextDate(values), options: [] }, values.length)}>
          Dodaj produkt
        </Button>
        <AddPackageProducts disabled={values.length > 0} onAdd={handleAddFromPackage} />
        <Button onClick={() => setMapOpen(true)}>Dodaj z mapy</Button>
        <Box
          sx={{
            flexGrow: 1,
          }}
        />
      </Stack>
      <AddProductFromMapDialog
        open={mapOpen}
        onClose={() => setMapOpen(false)}
        onAddProduct={handleAddFromMap}
        date={getNextDate(values)}
      />
    </>
  );
};

export default AddProducts;
